<template>
  <DSDialog
    :open="isOpen"
    @update:open="handleUpdateOpen"
  >
    <DSDialogContent class="gap-0">
      <DSDialogHeader class="border-b border-border">
        <DSDialogTitle class="text-xl">
          Additional instructions
        </DSDialogTitle>
        <DSDialogDescription v-if="instructions.text">
          {{ instructions.text }}
        </DSDialogDescription>
        <DSDialogDescription
          v-else
          class="sr-only"
        >
          Steps to complete at your institution before this connection will work.
        </DSDialogDescription>
      </DSDialogHeader>

      <div class="flex flex-col gap-4 px-6 py-5">
        <ol class="flex flex-col gap-2 pl-5 list-decimal marker:text-muted-foreground marker:font-bold">
          <li
            v-for="(step, index) in instructions.steps"
            :key="index"
            class="text-sm font-medium leading-normal text-foreground [&_a]:text-brand [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:no-underline"
            v-html="DOMPurify.sanitize(step)"
          />
        </ol>

        <label
          v-if="instructions.confirmationText"
          class="flex items-start gap-2.5 cursor-pointer select-none"
        >
          <DSCheckbox
            v-model="agreement"
            class="mt-0.5"
          />
          <span class="text-xs font-medium leading-normal text-muted-foreground">
            {{ instructions.confirmationText }}
          </span>
        </label>
      </div>

      <DSDialogFooter class="bg-background border-t border-border">
        <DSButton
          class="w-full"
          :disabled="isDisabled"
          @click="handleContinue"
        >
          Continue
        </DSButton>
      </DSDialogFooter>
    </DSDialogContent>
  </DSDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import DOMPurify from 'dompurify'

import type { ModalClass } from '@types'
import type { ConnectorInstructions } from '@/views/LinkedData/utils/types'

import {
  DSButton,
  DSCheckbox,
  DSDialog,
  DSDialogContent,
  DSDialogDescription,
  DSDialogFooter,
  DSDialogHeader,
  DSDialogTitle,
} from '@ds'

type Props = {
  modal: ModalClass<any>
  instructions: ConnectorInstructions
}

type Emits = {
  cancel: []
  continue: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const agreement = ref(false)

const isOpen = computed(() => props.modal.isOpened)

const isDisabled = computed(
  () => !!props.instructions.confirmationText && !agreement.value,
)

const handleClose = () => {
  props.modal.close()
  agreement.value = false
}

const handleUpdateOpen = (value: boolean) => {
  if (!value) handleCancel()
}

const handleCancel = () => {
  handleClose()
  emit('cancel')
}

const handleContinue = () => {
  if (isDisabled.value) return
  handleClose()
  emit('continue')
}
</script>
